import React, { useEffect, useState } from "react";
import API from "../services/api";

/* ------------------------------------------------------------------
  Helper utilities
-------------------------------------------------------------------*/
const safelyParseFloat = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

function formatClientName(clientName) {
  if (!clientName) return "";
  if (clientName.includes(",")) {
    const [last, first] = clientName.split(",").map((s) => s.trim());
    return `${first} ${last}`;
  }
  return clientName;
}

function getLocalYYYYMMDD() {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().split("T")[0];
}

const safelyFormatDate = (dateString) => {
  if (!dateString) return "";
  try {
    return dateString.slice(0, 10);
  } catch (err) {
    console.error("safelyFormatDate error:", err);
    return "";
  }
};

// Known service options (so unknown maps to Other)
const KNOWN_SERVICES = [
  "90834 - Individual Psychotherapy 50 min",
  "90847 - Family Psychotherapy 50 min",
  "90853 - Group Psychotherapy 50",
  "90832 - Individual Psychotherapy 30 min",
  "99354 - Add-on/Additional 30 min",
  "No Show",
  "Cancellation",
  "Other",
];

// Map CPT codes to canonical dropdown labels. Helps auto-select the correct
// service type when the last service label may include variations or extra text.
const CPT_TO_LABEL = {
  "90834": "90834 - Individual Psychotherapy 50 min",
  "90847": "90847 - Family Psychotherapy 50 min",
  "90853": "90853 - Group Psychotherapy 50",
  "90832": "90832 - Individual Psychotherapy 30 min",
  "99354": "99354 - Add-on/Additional 30 min",
};

/**
 * Attempt to coerce an arbitrary service label into one of the known options.
 * - First, extract any 5‑digit CPT code anywhere in the string and map it.
 * - Otherwise, perform a case/whitespace/punctuation‑insensitive match against KNOWN_SERVICES.
 * Returns the canonical label or null if no match is found.
 */
function coerceToKnownService(label) {
  if (!label) return null;
  const s = String(label);
  // 1) Find any 5-digit CPT code in the string
  const codeMatch = s.match(/(\d{5})/);
  if (codeMatch && CPT_TO_LABEL[codeMatch[1]]) {
    return CPT_TO_LABEL[codeMatch[1]];
  }
  // 2) Normalize the input label: lower-case, strip punctuation, collapse spaces
  const normalize = (str) =>
    str
      .toLowerCase()
      .replace(/[^\w\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  const norm = normalize(s);
  for (const opt of KNOWN_SERVICES) {
    // Skip catch-all and non-billable types in normalization
    if (opt === "Other" || opt === "No Show" || opt === "Cancellation") continue;
    const optNorm = normalize(opt);
    if (norm === optNorm) {
      return opt;
    }
  }
  return null;
}

/* ------------------------------------------------------------------
  Component
-------------------------------------------------------------------*/
function TherapistMultiClientDailyLogPage() {
  const today = getLocalYYYYMMDD();

  // Up to 10 fresh entries
  const initialEntries = Array.from({ length: 10 }, () => ({
    clientName: "",
    serviceType: "90834 - Individual Psychotherapy 50 min",
    customService: "",
    billedAmount: "",
    collectedAmount: "0",
    paymentType: "Card",
    notes: "",
    sessionDate: today,
    noShow: false,
    cancel: false,
  }));

  const [entries, setEntries] = useState(initialEntries);
  const [clients, setClients] = useState([]);
  const [clientBillingHistory, setClientBillingHistory] = useState({});

  // Add new client
  const [newClientFirstName, setNewClientFirstName] = useState("");
  const [newClientLastName, setNewClientLastName] = useState("");

  // Unpaid (pending admin approval)
  const [unpaidLogs, setUnpaidLogs] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({
    serviceType: "",
    billedAmount: "",
    collectedAmount: "",
    paymentType: "",
    notes: "",
    sessionDate: "",
  });

  // Submitted (read-only)
  const [submittedLogs, setSubmittedLogs] = useState([]);

  // Approved history (search on demand)
  const [approvedLogs, setApprovedLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState(today);
  const [filterClient, setFilterClient] = useState("");
  const [loading, setLoading] = useState(false);

  /* -------------------------- Data fetch ---------------------------*/
  const fetchClients = async () => {
    try {
      const res = await API.get("/therapist/clients");
      const list = (res.data || []).sort((a, b) =>
        (a.client_name || "").localeCompare(b.client_name || "")
      );
      setClients(list);
      await fetchClientBillingHistory(list);
    } catch (err) {
      console.error("fetchClients:", err);
      alert("Error fetching clients");
    }
  };

  const fetchClientBillingHistory = async (clientsList) => {
    const history = {};
    // Gather last session (non-cancel, non-no-show) per client
    await Promise.all(
      clientsList.map(async (c) => {
        // prefer explicit client-history endpoint, then fallbacks
        const tryEndpoints = [
          `/therapist/client-history/${c.id}?limit=1`,
          `/therapist/daily-logs/history?clientId=${c.id}&limit=1`,
        ];
        for (const ep of tryEndpoints) {
          try {
            const r = await API.get(ep);
            const arr = r.data || [];
            if (arr.length > 0) {
              const most = arr[0];
              if (most.service_type !== "Cancellation" && most.service_type !== "No Show") {
                history[c.client_name] = {
                  lastBilledAmount:
                    most.billed_amount !== null && most.billed_amount !== undefined
                      ? String(most.billed_amount)
                      : "0",
                  lastServiceType: most.service_type,
                };
              }
              break;
            }
          } catch (err) {
            // keep trying other endpoints
          }
        }
      })
    );
    setClientBillingHistory(history);
  };

  const fetchUnpaidLogs = async () => {
    try {
      const res = await API.get("/therapist/daily-logs/unpaid");
      setUnpaidLogs(res.data || []);
    } catch (err) {
      console.error("fetchUnpaidLogs:", err);
      setUnpaidLogs([]);
    }
  };

  const fetchSubmittedLogs = async () => {
    try {
      const res = await API.get("/therapist/daily-logs/submitted");
      setSubmittedLogs(res.data || []);
    } catch (e1) {
      try {
        const r2 = await API.get("/therapist/daily-logs?status=SUBMITTED");
        setSubmittedLogs(r2.data || []);
      } catch (e2) {
        try {
          const r3 = await API.get("/therapist/daily-logs/history?status=SUBMITTED");
          setSubmittedLogs(r3.data || []);
        } catch (e3) {
          console.error("fetchSubmittedLogs:", e1, e2, e3);
          setSubmittedLogs([]);
        }
      }
    }
  };

  const fetchApprovedLogs = async () => {
    setLoading(true);
    try {
      if (!searchTerm && !startDate && !endDate && !filterClient) {
        setApprovedLogs([]);
        setLoading(false);
        return;
      }
      let url = "/therapist/daily-logs/approved?";
      if (searchTerm) url += `search=${encodeURIComponent(searchTerm)}&`;
      if (startDate) url += `startDate=${startDate}&`;
      if (endDate) url += `endDate=${endDate}&`;
      if (filterClient) url += `client=${encodeURIComponent(filterClient)}&`;
      const res = await API.get(url);
      setApprovedLogs(res.data || []);
    } catch (err) {
      console.error("fetchApprovedLogs:", err);
      alert("Error fetching approved logs.");
    } finally {
      setLoading(false);
    }
  };

  /* ----------------------- Add new client --------------------------*/
  const handleAddNewClient = async () => {
    const fn = newClientFirstName.trim();
    const ln = newClientLastName.trim();
    if (!fn || !ln) {
      alert("Please enter both first and last name.");
      return;
    }
    const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
    const combined = `${cap(ln)}, ${cap(fn)}`;
    const exists = clients.find((c) => (c.client_name || "").toLowerCase() === combined.toLowerCase());
    if (exists) {
      alert("A client with this name already exists.");
      return;
    }
    try {
      await API.post("/therapist/clients", { clientName: combined });
      alert(`Created new client: ${combined}`);
      setNewClientFirstName("");
      setNewClientLastName("");
      fetchClients();
    } catch (err) {
      console.error("handleAddNewClient:", err);
      alert("Error adding client.");
    }
  };

  /* ---------------------- Bulk entry form --------------------------*/
  const handleChange = (index, field, value) => {
    setEntries((prev) => {
      const next = [...prev];

      if (field === "clientName") {
        next[index] = { ...next[index], clientName: value };
        // Auto-populate last billed + service type
        const hist = clientBillingHistory[value];
        if (hist) {
          if (hist.lastBilledAmount !== undefined) {
            next[index].billedAmount = hist.lastBilledAmount;
          }
              const svc = hist.lastServiceType;
              if (svc && svc !== "No Show" && svc !== "Cancellation") {
                // Normalize the last service label to a known dropdown option if possible
                const canonical = coerceToKnownService(svc);
                if (canonical) {
                  next[index].serviceType = canonical;
                  next[index].customService = "";
                } else {
                  // Fall back to "Other" and store the original label in customService
                  next[index].serviceType = "Other";
                  next[index].customService = svc;
                }
              }
        }
        return next;
      }

      if (field === "noShow" && value === true) {
        next[index] = {
          ...next[index],
          noShow: true,
          cancel: false,
          serviceType: "No Show",
          collectedAmount: "0",
        };
        return next;
      }

      if (field === "cancel" && value === true) {
        next[index] = {
          ...next[index],
          cancel: true,
          noShow: false,
          serviceType: "Cancellation",
          billedAmount: "0",
          collectedAmount: "0",
        };
        return next;
      }

      if ((field === "noShow" || field === "cancel") && value === false) {
        // turn off flag, restore default service if it was one of the flags
        const curr = next[index];
        next[index] = { ...curr, [field]: false };
        if (curr.serviceType === "No Show" || curr.serviceType === "Cancellation") {
          next[index].serviceType = "90834 - Individual Psychotherapy 50 min";
        }
        return next;
      }

      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = entries
      .filter((en) => en.clientName && en.sessionDate)
      .map((en) => {
        let finalService = en.serviceType;
        if (finalService === "Other" && en.customService.trim()) finalService = en.customService.trim();
        return {
          clientName: en.clientName,
          serviceType: finalService,
          billedAmount: en.billedAmount || 0,
          collectedAmount: en.collectedAmount || 0,
          paymentType: (en.paymentType || "").toUpperCase(),
          notes: en.notes || "",
          sessionDate: en.sessionDate,
          noShow: !!en.noShow,
          cancelation: !!en.cancel, // backend field name
        };
      });

    if (payload.length === 0) {
      alert("No valid entries to submit.");
      return;
    }

    try {
      const res = await API.post("/therapist/daily-log/bulk", { entries: payload });
      alert(`Success! Inserted ${res.data.count} sessions.`);

      // Update local history for quick recall next time
      const hist = { ...clientBillingHistory };
      payload.forEach((en) => {
        if (en.clientName && !en.cancelation) {
          hist[en.clientName] = {
            lastBilledAmount: String(en.billedAmount || 0),
            lastServiceType: en.serviceType,
          };
        }
      });
      setClientBillingHistory(hist);

      setEntries(initialEntries);
      // Refresh lists
      fetchUnpaidLogs();
      fetchSubmittedLogs();
    } catch (err) {
      console.error("handleSubmit bulk:", err);
      alert("Error submitting entries.");
    }
  };

  /* ------------------ Edit / delete unpaid rows --------------------*/
  const startEdit = (log) => {
    setEditId(log.id);
    setEditForm({
      serviceType: log.service_type,
      billedAmount: log.billed_amount,
      collectedAmount: log.collected_amount,
      paymentType: log.payment_type,
      notes: log.notes || "",
      sessionDate: safelyFormatDate(log.session_date),
    });
  };

  const saveEdit = async () => {
    try {
      await API.put(`/therapist/daily-logs/${editId}`, {
        serviceType: editForm.serviceType,
        billedAmount: editForm.billedAmount,
        collectedAmount: editForm.collectedAmount,
        paymentType: editForm.paymentType,
        notes: editForm.notes,
        sessionDate: editForm.sessionDate,
      });
      setEditId(null);
      fetchUnpaidLogs();
      fetchSubmittedLogs();
    } catch (err) {
      console.error("saveEdit:", err);
      alert("Error saving edits.");
    }
  };

  const cancelEdit = () => setEditId(null);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await API.delete(`/therapist/daily-logs/${id}`);
      fetchUnpaidLogs();
      fetchSubmittedLogs();
    } catch (err) {
      console.error("handleDelete:", err);
      alert("Error deleting entry.");
    }
  };

  /* ------------------- Approved history search ---------------------*/
  const handleSearch = (e) => {
    e.preventDefault();
    fetchApprovedLogs();
  };

  const resetFilters = () => {
    setSearchTerm("");
    setStartDate("");
    setEndDate(today);
    setFilterClient("");
    fetchApprovedLogs();
  };

  /* --------------------------- Effects -----------------------------*/
  useEffect(() => {
    fetchClients();
    fetchUnpaidLogs();
    fetchSubmittedLogs();
    // approved logs fetched on demand
  }, []);

  /* ------------------------------ UI --------------------------------*/
  return (
    <div
      style={{
        margin: "1rem",
        background: "#f0f5ff",
        padding: "1rem",
        fontFamily: "Arial, sans-serif",
        borderRadius: "8px",
        boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
      }}
    >
      <style>{`
        input[type=number]::-webkit-outer-spin-button,
        input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        input[type=number] { -moz-appearance: textfield; }
        input, select, button { padding: 0.5rem; border: 1px solid #ccc; border-radius: 4px; font-size: 0.9rem; }
        button { background: #3b82f6; color: white; border: none; cursor: pointer; margin-right: 0.5rem; transition: background 0.2s; }
        button:hover { background: #1D2C5F; }
        button.delete { background: #ef4444; }
        button.delete:hover { background: #dc2626; }
        .form-section { border: 1px solid #d1d5db; padding: 1.5rem; margin-bottom: 1.5rem; background: white; border-radius: 6px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
        .form-section h3 { margin-top: 0; color: #1e40af; border-bottom: 2px solid #dbeafe; padding-bottom: 0.5rem; }
        table { border-collapse: collapse; width: 100%; background: white; border-radius: 6px; overflow: hidden; }
        th { background: #1D2C5F; color: white; text-align: left; padding: 0.75rem; }
        td { padding: 0.75rem; border-bottom: 1px solid #e5e7eb; }
        tbody tr:hover { background: #f1f5f9; }
        .unpaid-card { background: #ffffff; margin-bottom: 1rem; padding: 1rem; border: 1px solid #ccc; border-radius: 6px; }
        .unpaid-card .item { margin-bottom: 0.5rem; }
        .unpaid-card .item span { font-weight: bold; margin-right: 0.25rem; }
        @media (max-width: 750px) {
          .desktop-only { display: none; }
        }
        @media (min-width: 751px) {
          .mobile-only { display: none; }
        }
      `}</style>

      <h2 style={{ color: "#1e40af", marginBottom: "1.5rem", textAlign: "center" }}>
        Therapist Daily Log Management
      </h2>

      {/* Instructions */}
      <div
        style={{
          marginBottom: "1.5rem",
          background: "#dbeafe",
          padding: "1rem",
          borderRadius: "6px",
          border: "1px solid #93c5fd",
        }}
      >
        <strong>Tip:</strong> Selecting a client will auto-fill their last billed amount and last service type (excluding
        cancellations and no-shows). Use <em>Other</em> to enter a custom CPT if needed.
      </div>

      {/* 1) Add New Client */}
      <div className="form-section">
        <h3>Add New Client</h3>
        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
          <div>
            <label style={{ display: "block", fontWeight: 600 }}>First Name</label>
            <input type="text" value={newClientFirstName} onChange={(e) => setNewClientFirstName(e.target.value)} />
          </div>
          <div>
            <label style={{ display: "block", fontWeight: 600 }}>Last Name</label>
            <input type="text" value={newClientLastName} onChange={(e) => setNewClientLastName(e.target.value)} />
          </div>
          <div style={{ alignSelf: "end" }}>
            <button onClick={handleAddNewClient}>Add Client</button>
          </div>
        </div>
      </div>

      {/* 2) Bulk Daily Log Table */}
      <div className="form-section">
        <h3>Enter New Sessions (Up to 10)</h3>
        <form onSubmit={handleSubmit}>
          <div style={{ overflowX: "auto" }}>
            <table>
              <thead>
                <tr>
                  <th>Client*</th>
                  <th>Service</th>
                  <th>Billed ($)</th>
                  <th>Collected ($)</th>
                  <th>Payment</th>
                  <th>Notes</th>
                  <th>Date*</th>
                  <th>No Show?</th>
                  <th>Cancel?</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, index) => (
                  <tr key={index}>
                    <td>
                      <select
                        value={entry.clientName}
                        onChange={(e) => handleChange(index, "clientName", e.target.value)}
                        style={{ width: "100%" }}
                      >
                        <option value="">-- Select Client --</option>
                        {clients.map((c) => (
                          <option key={c.id} value={c.client_name}>
                            {c.client_name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        value={entry.serviceType}
                        onChange={(e) => handleChange(index, "serviceType", e.target.value)}
                        style={{ width: "100%" }}
                        disabled={entry.noShow || entry.cancel}
                      >
                        <option value="90834 - Individual Psychotherapy 50 min">90834 - Individual Psychotherapy 50 min</option>
                        <option value="90847 - Family Psychotherapy 50 min">90847 - Family Psychotherapy 50 min</option>
                        <option value="90853 - Group Psychotherapy 50">90853 - Group Psychotherapy 50</option>
                        <option value="90832 - Individual Psychotherapy 30 min">90832 - Individual Psychotherapy 30 min</option>
                        <option value="99354 - Add-on/Additional 30 min">99354 - Add-on/Additional 30 min</option>
                        <option value="No Show">No Show</option>
                        <option value="Cancellation">Cancellation</option>
                        <option value="Other">Other (enter custom)</option>
                      </select>
                      {entry.serviceType === "Other" && (
                        <div style={{ marginTop: "0.5rem" }}>
                          <input
                            type="text"
                            placeholder="Enter custom service/CPT"
                            value={entry.customService}
                            onChange={(e) => handleChange(index, "customService", e.target.value)}
                            style={{ width: "100%" }}
                          />
                        </div>
                      )}
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={entry.billedAmount}
                        onChange={(e) => handleChange(index, "billedAmount", e.target.value)}
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={entry.collectedAmount}
                        onChange={(e) => handleChange(index, "collectedAmount", e.target.value)}
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td>
                      <select
                        value={entry.paymentType}
                        onChange={(e) => handleChange(index, "paymentType", e.target.value)}
                        style={{ width: "100%" }}
                      >
                        <option value="Card">Card</option>
                        <option value="Cash">Cash</option>
                        <option value="Check">Check</option>
                      </select>
                    </td>
                    <td>
                      <input
                        type="text"
                        value={entry.notes}
                        onChange={(e) => handleChange(index, "notes", e.target.value)}
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td>
                      <input
                        type="date"
                        value={entry.sessionDate}
                        onChange={(e) => handleChange(index, "sessionDate", e.target.value)}
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={entry.noShow}
                        onChange={(e) => handleChange(index, "noShow", e.target.checked)}
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={entry.cancel}
                        onChange={(e) => handleChange(index, "cancel", e.target.checked)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button type="submit" style={{ marginTop: "1rem", padding: "0.6rem 1.5rem", fontSize: "1rem", fontWeight: 700 }}>
            Submit Entries
          </button>
        </form>
      </div>

      {/* 3) Pending Admin Approval (UNPAID) */}
      <div className="form-section">
        <h3>Pending Admin Approval</h3>
        {unpaidLogs.length === 0 ? (
          <p style={{ textAlign: "center", padding: "1rem" }}>No pending entries found.</p>
        ) : (
          <>
            <div className="desktop-only" style={{ overflowX: "auto" }}>
              <table>
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Service</th>
                    <th>Billed ($)</th>
                    <th>Collected ($)</th>
                    <th>Payment</th>
                    <th>Notes</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {unpaidLogs.map((log) => {
                    const isEditing = editId === log.id;
                    if (isEditing) {
                      return (
                        <tr key={log.id}>
                          <td>{formatClientName(log.client_name)}</td>
                          <td>
                            <input
                              type="text"
                              value={editForm.serviceType}
                              onChange={(e) => setEditForm({ ...editForm, serviceType: e.target.value })}
                              style={{ width: "100%" }}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              value={editForm.billedAmount}
                              onChange={(e) => setEditForm({ ...editForm, billedAmount: e.target.value })}
                              style={{ width: "100%" }}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              value={editForm.collectedAmount}
                              onChange={(e) => setEditForm({ ...editForm, collectedAmount: e.target.value })}
                              style={{ width: "100%" }}
                            />
                          </td>
                          <td>
                            <select
                              value={editForm.paymentType}
                              onChange={(e) => setEditForm({ ...editForm, paymentType: e.target.value })}
                              style={{ width: "100%" }}
                            >
                              <option value="CARD">Card</option>
                              <option value="CASH">Cash</option>
                              <option value="CHECK">Check</option>
                            </select>
                          </td>
                          <td>
                            <input
                              type="text"
                              value={editForm.notes}
                              onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                              style={{ width: "100%" }}
                            />
                          </td>
                          <td>
                            <input
                              type="date"
                              value={editForm.sessionDate}
                              onChange={(e) => setEditForm({ ...editForm, sessionDate: e.target.value })}
                              style={{ width: "100%" }}
                            />
                          </td>
                          <td>
                            <button onClick={saveEdit}>Save</button>
                            <button onClick={cancelEdit} style={{ background: "#6b7280" }}>Cancel</button>
                          </td>
                        </tr>
                      );
                    }
                    return (
                      <tr key={log.id}>
                        <td>{formatClientName(log.client_name)}</td>
                        <td>{log.service_type}</td>
                        <td>${safelyParseFloat(log.billed_amount).toFixed(2)}</td>
                        <td>${safelyParseFloat(log.collected_amount).toFixed(2)}</td>
                        <td>{log.payment_type}</td>
                        <td>{log.notes || ""}</td>
                        <td>{safelyFormatDate(log.session_date)}</td>
                        <td>
                          <button onClick={() => startEdit(log)}>Edit</button>
                          <button className="delete" onClick={() => handleDelete(log.id)}>Delete</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="mobile-only">
              {unpaidLogs.map((log) => {
                const isEditing = editId === log.id;
                if (isEditing) {
                  return (
                    <div className="unpaid-card" key={log.id}>
                      <div className="item"><span>Client:</span> {formatClientName(log.client_name)}</div>
                      <div className="item"><span>Service:</span> <input type="text" value={editForm.serviceType} onChange={(e) => setEditForm({ ...editForm, serviceType: e.target.value })} style={{ width: "100%" }} /></div>
                      <div className="item"><span>Billed:</span> <input type="number" value={editForm.billedAmount} onChange={(e) => setEditForm({ ...editForm, billedAmount: e.target.value })} style={{ width: "100%" }} /></div>
                      <div className="item"><span>Collected:</span> <input type="number" value={editForm.collectedAmount} onChange={(e) => setEditForm({ ...editForm, collectedAmount: e.target.value })} style={{ width: "100%" }} /></div>
                      <div className="item"><span>Payment:</span> <select value={editForm.paymentType} onChange={(e) => setEditForm({ ...editForm, paymentType: e.target.value })} style={{ width: "100%" }}><option value="CARD">Card</option><option value="CASH">Cash</option><option value="CHECK">Check</option></select></div>
                      <div className="item"><span>Notes:</span> <input type="text" value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} style={{ width: "100%" }} /></div>
                      <div className="item"><span>Date:</span> <input type="date" value={editForm.sessionDate} onChange={(e) => setEditForm({ ...editForm, sessionDate: e.target.value })} style={{ width: "100%" }} /></div>
                      <div className="item" style={{ marginTop: "0.75rem" }}>
                        <button onClick={saveEdit}>Save</button>
                        <button onClick={cancelEdit} style={{ background: "#6b7280" }}>Cancel</button>
                      </div>
                    </div>
                  );
                }
                return (
                  <div className="unpaid-card" key={log.id}>
                    <div className="item"><span>Client:</span> {formatClientName(log.client_name)}</div>
                    <div className="item"><span>Service:</span> {log.service_type}</div>
                    <div className="item"><span>Billed:</span> ${safelyParseFloat(log.billed_amount).toFixed(2)}</div>
                    <div className="item"><span>Collected:</span> ${safelyParseFloat(log.collected_amount).toFixed(2)}</div>
                    <div className="item"><span>Payment:</span> {log.payment_type}</div>
                    <div className="item"><span>Notes:</span> {log.notes || ""}</div>
                    <div className="item"><span>Date:</span> {safelyFormatDate(log.session_date)}</div>
                    <div className="item" style={{ marginTop: "0.75rem" }}>
                      <button onClick={() => startEdit(log)}>Edit</button>
                      <button className="delete" onClick={() => handleDelete(log.id)}>Delete</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* 3.5) Submitted (read-only) */}
      <div className="form-section">
        <h3>Submitted to Clearinghouse for Approval</h3>
        {submittedLogs.length === 0 ? (
          <p style={{ textAlign: "center", padding: "1rem" }}>No sessions currently submitted.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table>
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Service</th>
                  <th>Billed ($)</th>
                  <th>Collected ($)</th>
                  <th>Payment</th>
                  <th>Notes</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {submittedLogs.map((log) => (
                  <tr key={log.id}>
                    <td>{formatClientName(log.client_name)}</td>
                    <td>{log.service_type}</td>
                    <td>${safelyParseFloat(log.billed_amount).toFixed(2)}</td>
                    <td>${safelyParseFloat(log.collected_amount).toFixed(2)}</td>
                    <td>{log.payment_type}</td>
                    <td>{log.notes || ""}</td>
                    <td>{safelyFormatDate(log.session_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 4) Approved history */}
      <div className="form-section">
        <h3>Approved Session History</h3>
        <div style={{ marginBottom: "1rem", background: "#f9fafb", padding: "0.75rem", borderRadius: 4, border: "1px solid #e5e7eb" }}>
          Search for previously approved sessions by entering search terms, selecting a client, or choosing a date range.
        </div>

        <form onSubmit={handleSearch} style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginBottom: "1rem" }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <label style={{ display: "block", fontWeight: 600 }}>Search</label>
            <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ width: "100%" }} placeholder="Client name, service…" />
          </div>
          <div style={{ width: 240 }}>
            <label style={{ display: "block", fontWeight: 600 }}>Client</label>
            <select value={filterClient} onChange={(e) => setFilterClient(e.target.value)} style={{ width: "100%" }}>
              <option value="">All Clients</option>
              {clients.map((c) => (
                <option key={c.id} value={c.client_name}>{formatClientName(c.client_name)}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: "block", fontWeight: 600 }}>From</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div>
            <label style={{ display: "block", fontWeight: 600 }}>To</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
          <div style={{ alignSelf: "end" }}>
            <button type="submit" style={{ marginRight: 8 }}>{loading ? "Searching…" : "Search"}</button>
            <button type="button" style={{ background: "#6b7280" }} onClick={resetFilters}>Reset</button>
          </div>
        </form>

        {loading ? (
          <div style={{ textAlign: "center", padding: "2rem" }}>Loading…</div>
        ) : approvedLogs.length === 0 ? (
          <p style={{ textAlign: "center", padding: "1rem" }}>No approved sessions found.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table>
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Service</th>
                  <th>Billed ($)</th>
                  <th>Collected ($)</th>
                  <th>Payment</th>
                  <th>Notes</th>
                  <th>Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {approvedLogs.map((log) => (
                  <tr key={log.id}>
                    <td>{formatClientName(log.client_name)}</td>
                    <td>{log.service_type}</td>
                    <td>${safelyParseFloat(log.billed_amount).toFixed(2)}</td>
                    <td>${safelyParseFloat(log.collected_amount).toFixed(2)}</td>
                    <td>{log.payment_type}</td>
                    <td>{log.notes || ""}</td>
                    <td>{safelyFormatDate(log.session_date)}</td>
                    <td>{log.no_show ? "No Show" : log.cancelation ? "Canceled" : "Approved"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && approvedLogs.length > 0 && (
          <div style={{ marginTop: "1rem", fontWeight: 600, color: "#4b5563" }}>Total Entries: {approvedLogs.length}</div>
        )}
      </div>
    </div>
  );
}

export default TherapistMultiClientDailyLogPage;